import { create } from "zustand";
import type { EstimateOption } from "../api/pricing";
import type { FundRequestPayoutInput } from "../api/fund-request";
import type { PayoutMethodType } from "../interfaces/payout";

export interface ComposePayoutSelection {
  method: PayoutMethodType;
  option: EstimateOption | null;
  payout: FundRequestPayoutInput;
}

/**
 * In-memory compose state for the request transfer screen. Lives in a module-level
 * zustand store (NOT persisted) so destination + payout selection survive
 * client-side navigation (e.g. AddCard → Transfer). Reset explicitly when a
 * fresh request starts (HomePage "new request").
 */
interface ComposeState {
  /** Editable destination country (receive leg). Null until seeded from config. */
  destinationCountry: string | null;
  setDestinationCountry: (country: string) => void;
  /** Editable destination currency (receive leg). Null until seeded from catalog. */
  destinationCurrency: string | null;
  setDestinationCurrency: (currency: string) => void;
  /** Chosen payout method + instrument details for the compose session. */
  selection: ComposePayoutSelection | null;
  setSelection: (selection: ComposePayoutSelection | null) => void;
  resetCompose: () => void;
}

const initialState = {
  destinationCountry: null as string | null,
  destinationCurrency: null as string | null,
  selection: null as ComposePayoutSelection | null,
};

export const useComposeStore = create<ComposeState>((set) => ({
  ...initialState,
  setDestinationCountry: (destinationCountry) =>
    set(() => ({ destinationCountry })),
  setDestinationCurrency: (destinationCurrency) =>
    set(() => ({ destinationCurrency })),
  setSelection: (selection) => set(() => ({ selection })),
  resetCompose: () => set(() => ({ ...initialState })),
}));
